import { z } from 'zod';

import { parseOrThrow } from '../../../api';

import {
  APPLY_OUTCOMES,
  ARCHETYPES,
  CADENCE_UNITS,
  DIRECTIONS,
  DUE_BASES,
  FORM_DERIVATIONS,
  FORM_MATCHES,
  FORM_RULE_READS,
  ITEM_KINDS,
  PERFORMED_BY,
  PLANT_TYPES,
  PROGRAMMES,
  READING_ATTRIBUTIONS,
  READING_KINDS,
  READING_PLACES,
  READING_SOURCES,
  SEVERITIES,
  STORED_SOURCES,
  TIERS,
  UNKNOWN,
  VERSION_ACTIONS,
  WARNING_CODES,
  WORK_TYPES,
} from '../constants/vocabulary';
import type { Unknown } from '../constants/vocabulary';

const isOneOf = <T extends string>(values: readonly T[], value: unknown): value is T =>
  typeof value === 'string' && (values as readonly string[]).includes(value);

const lenient = <T extends string>(values: readonly T[]) =>
  z.unknown().transform((value): T | Unknown => (isOneOf(values, value) ? value : UNKNOWN));

const lenientOrNull = <T extends string>(values: readonly T[]) =>
  z.unknown().transform((value): T | Unknown | null => {
    if (value === null || value === undefined) return null;
    return isOneOf(values, value) ? value : UNKNOWN;
  });

const knownOnly = <T extends string>(values: readonly T[]) =>
  z
    .array(z.unknown())
    .default([])
    .transform((items): T[] => items.filter((item): item is T => isOneOf(values, item)));

const text = z.string();
const nullableText = z.string().nullable().default(null);
const nullableNumber = z.number().nullable().default(null);
const instant = z.string();
const readingName = z.string().min(1);
const severity = z.enum(SEVERITIES);
const direction = z.enum(DIRECTIONS);

export const severityBandSchema = z.object({ severity, threshold: z.number() }).passthrough();

export const cadenceSchema = z.object({ n: z.number().min(1), unit: z.enum(CADENCE_UNITS) }).passthrough();

const validRangeSchema = z.object({ min: z.number(), max: z.number() }).passthrough();

export const libraryReadingSchema = z
  .object({
    name: readingName,
    tag: text,
    unit: text.default(''),
    kind: z.enum(READING_KINDS),
    valid: validRangeSchema,
    direction: direction.nullable().default(null),
  })
  .passthrough();

export const libraryAlertSchema = z
  .object({
    key: text,
    name: text,
    reading: readingName,
    direction,
    bands: z.array(severityBandSchema).default([]),
    ifDrawn: z.boolean().default(false),
    gate: readingName.nullable().default(null),
    flowKey: nullableText,
    causeKey: nullableText,
  })
  .passthrough();

export const libraryScheduleSchema = z
  .object({
    key: text,
    name: text,
    workType: z.enum(WORK_TYPES),
    basis: z.enum(DUE_BASES).default('CALENDAR'),
    every: cadenceSchema.nullable().default(null),
    meterHours: nullableNumber,
    hours: nullableNumber,
    parts: nullableText,
    performedBy: z.enum(PERFORMED_BY).default('IN_HOUSE'),
    statutory: z.boolean().default(false),
    verify: z.boolean().default(false),
    flowKey: nullableText,
    causeKey: nullableText,
  })
  .passthrough();

export const conditionJobSchema = z
  .object({
    key: text,
    name: text,
    reading: readingName,
    doWhen: z.number(),
    doneWhen: z.number(),
    flowKey: nullableText,
    causeKey: nullableText,
  })
  .passthrough();

const nextPhaseReadingSchema = z.object({ name: readingName, note: text.default('') }).passthrough();

const nextPhaseAlertSchema = z
  .object({
    name: text,
    reading: readingName,
    note: text.default(''),
    direction,
    bands: z.array(severityBandSchema).default([]),
    flowKey: nullableText,
    causeKey: nullableText,
  })
  .passthrough();

const nextPhaseWorkSchema = z
  .object({ name: text, note: text.default(''), reading: readingName.nullable().default(null) })
  .passthrough();

export const nextPhaseSchema = z
  .object({
    readings: z.array(nextPhaseReadingSchema).default([]),
    alerts: z.array(nextPhaseAlertSchema).default([]),
    work: z.array(nextPhaseWorkSchema).default([]),
  })
  .passthrough();

const formAnswerMatchSchema = z
  .object({
    op: z.enum(FORM_MATCHES).default('ANSWERED'),
    value: nullableText,
    flags: nullableText,
  })
  .passthrough();

const formRuleAdjustSchema = z
  .object({
    itemKey: text,
    performedBy: z.enum(PERFORMED_BY).nullable().default(null),
    note: nullableText,
    doWhenFactor: nullableNumber,
    doneWhenFactor: nullableNumber,
    partsNote: nullableText,
  })
  .passthrough();

export const formRuleSchema = z
  .object({
    questionKey: nullableText,
    question: text,
    reads: z.enum(FORM_RULE_READS),
    when: formAnswerMatchSchema.default({ op: 'ANSWERED', value: null, flags: null }),
    field: nullableText,
    derive: z.enum(FORM_DERIVATIONS).nullable().default(null),
    key: nullableText,
    rawKey: nullableText,
    uses: z.array(text).default([]),
    reading: readingName.nullable().default(null),
    direction: direction.nullable().default(null),
    factors: z.array(z.object({ severity, factor: z.number() }).passthrough()).default([]),
    alert: libraryAlertSchema.nullable().default(null),
    schedule: libraryScheduleSchema.nullable().default(null),
    everyBy: z.array(z.object({ pattern: text, flags: nullableText, every: cadenceSchema }).passthrough()).default([]),
    partsBy: z.array(z.object({ question: text, pattern: text, flags: nullableText, note: text }).passthrough()).default([]),
    adjust: formRuleAdjustSchema.nullable().default(null),
    why: nullableText,
    says: text,
  })
  .passthrough();

export const libraryTypeSchema = z
  .object({
    _id: text.optional(),
    code: text,
    name: text,
    archetype: z.enum(ARCHETYPES),
    holdsWater: z.boolean().default(false),
    tier: z.enum(TIERS),
    expects: z.array(z.enum(PROGRAMMES)).default([]),
    tagPrefix: text,
    readings: z.array(libraryReadingSchema).default([]),
    tagMap: z.record(text, readingName).default({}),
    alerts: z.array(libraryAlertSchema).default([]),
    schedules: z.array(libraryScheduleSchema).default([]),
    conditionJobs: z.array(conditionJobSchema).default([]),
    formRules: z.array(formRuleSchema).default([]),
    nextPhase: nextPhaseSchema.default(() => ({ readings: [], alerts: [], work: [] })),
    cycles: z.boolean().default(false),
    confirm: nullableText,
    version: z.number().int().min(1),
    seededFrom: nullableText,
    isArchived: z.boolean().default(false),
    createdBy: nullableText,
    updatedBy: nullableText,
    createdAt: instant.optional(),
    updatedAt: instant.optional(),
  })
  .passthrough();

const countsSchema = z.object({
  readings: z.number().default(0),
  alerts: z.number().default(0),
  schedules: z.number().default(0),
  conditionJobs: z.number().default(0),
  formRules: z.number().default(0),
  nextPhase: z.number().default(0),
});

export const libraryTypeSummarySchema = z.object({
  code: text,
  name: text,
  archetype: lenient(ARCHETYPES),
  tier: lenient(TIERS),
  holdsWater: z.boolean().default(false),
  expects: knownOnly(PROGRAMMES),
  confirm: nullableText,
  seededFrom: nullableText,
  version: z.number(),
  counts: countsSchema,
  updatedAt: instant.nullable().default(null),
});

export const libraryListSchema = z.object({ items: z.array(libraryTypeSummarySchema) });

const versionSummarySchema = z.object({
  added: z.array(text).default([]),
  removed: z.array(text).default([]),
  changed: z.array(text).default([]),
  confirmed: z.boolean().default(false),
});

export const libraryVersionSchema = z.object({
  _id: text.optional(),
  code: text,
  version: z.number(),
  action: lenient(VERSION_ACTIONS),
  reason: text,
  changedBy: nullableText,
  changedByLabel: text.default(''),
  snapshot: z.record(text, z.unknown()).default({}),
  summary: versionSummarySchema.default({ added: [], removed: [], changed: [], confirmed: false }),
  createdAt: instant,
});

export const libraryHistorySchema = z.object({ code: text, versions: z.array(libraryVersionSchema) });

export const readingDefSchema = z.object({
  name: readingName,
  where: lenient(READING_PLACES),
  tag: text,
  unit: text.default(''),
  kind: lenient(READING_KINDS),
  valid: validRangeSchema,
  direction: direction.nullable().default(null),
  inWater: z.boolean().default(false),
  purposeAbbrs: z.array(text).default([]),
  widgetKinds: z.array(text).default([]),
  note: nullableText,
  expr: nullableText,
});

export const readingsSchema = z.object({ items: z.array(readingDefSchema) });

export const plantTypeProfileSchema = z.object({
  _id: text.optional(),
  code: lenient(PLANT_TYPES),
  name: text,
  usualTypeCodes: z.array(text).default([]),
  rounds: z.array(libraryScheduleSchema).default([]),
  milestones: z.array(z.object({ key: text, name: text })).default([]),
  version: z.number().default(1),
});

export const plantTypesSchema = z.object({ items: z.array(plantTypeProfileSchema) });

const machineCount = z.number().int().min(0);

export const libraryPlantSchema = z.object({
  plantId: text,
  plantName: z.string().nullable(),
  machines: machineCount,
  stale: machineCount,
});

export const libraryPlantsSchema = z.object({ items: z.array(libraryPlantSchema) });

export const nodeTypeOptionSchema = z.object({ code: text, name: text, hasContent: z.boolean() });

export const nodeTypesSchema = z.object({ items: z.array(nodeTypeOptionSchema) });

const tierOverrideSchema = z.object({
  tier: lenient(TIERS),
  reason: text,
  by: nullableText,
  at: instant,
});

export const machineOverrideSchema = z.object({
  kind: lenient(ITEM_KINDS),
  key: text,
  off: z.boolean().default(false),
  direction: direction.nullable().default(null),
  bands: z.array(severityBandSchema).optional(),
  basis: z.enum(DUE_BASES).nullable().default(null),
  every: cadenceSchema.nullable().default(null),
  meterHours: nullableNumber,
  performedBy: z.enum(PERFORMED_BY).nullable().default(null),
  doWhen: nullableNumber,
  doneWhen: nullableNumber,
  reason: text,
  by: nullableText,
  at: instant,
});

export const maintenanceSchema = z.object({
  reason: text,
  plannedHours: z.number(),
  note: text.default(''),
  since: instant,
  by: nullableText,
  suppressedAlertKeys: z.array(text).default([]),
});

export const registryEntrySchema = z.object({
  _id: text,
  plantId: text,
  layoutEquipmentId: text,
  layoutCellId: nullableText,
  name: text.default(''),
  tag: text,
  drawnCode: text,
  storedCode: nullableText,
  storedSource: lenientOrNull(STORED_SOURCES),
  recordNodeType: nullableText,
  typeCode: text,
  libraryTypeId: nullableText,
  libraryVersion: nullableNumber,
  tierOverride: tierOverrideSchema.nullable().default(null),
  derived: z.record(text, z.number()).default({}),
  flags: z.record(text, z.union([text, z.boolean()])).default({}),
  overrides: z.array(machineOverrideSchema).default([]),
  maintenance: maintenanceSchema.nullable().default(null),
  lastSyncedAt: instant.nullable().default(null),
  isArchived: z.boolean().default(false),
  version: z.number().int().min(0),
  inDatabase: z.boolean(),
  stale: z.boolean(),
  libraryVersionCurrent: nullableNumber,
  createdAt: instant.optional(),
  updatedAt: instant.optional(),
});

export const registryListSchema = z.object({ items: z.array(z.unknown()) });

export interface RegistryList {
  readonly entries: readonly z.output<typeof registryEntrySchema>[];
  readonly unreadable: number;
  readonly issues: readonly z.ZodIssue[];
}

export const parseRegistryList = (raw: unknown): RegistryList => {
  const results = parseOrThrow(registryListSchema, raw, 'machine list').items.map((item) => registryEntrySchema.safeParse(item));
  const entries = results.flatMap((result) => (result.success ? [result.data] : []));
  const issues = results.flatMap((result) => (result.success ? [] : result.error.issues));
  return { entries, unreadable: results.length - entries.length, issues };
};

export const registryEntryDetailSchema = z.object({
  entry: registryEntrySchema,
  library: libraryTypeSchema.nullable(),
});

const answerSchema = z.object({
  questionId: text,
  question: text.default(''),
  semanticKey: nullableText,
  answer: text.default(''),
});

const machineReadingViewSchema = z.object({
  sensorId: nullableText,
  sensorTag: nullableText,
  purpose: nullableText,
  reading: nullableText,
  name: text,
  unit: text.default(''),
  where: lenientOrNull(READING_PLACES),
  nextPhase: z.boolean().default(false),
  note: nullableText,
  source: lenient(READING_SOURCES),
  widget: nullableText,
  widgetCellId: nullableText,
  linkCellId: nullableText,
  attribution: lenient(READING_ATTRIBUTIONS),
});

const previewItemSchema = z.object({ kind: lenient(ITEM_KINDS), key: text, name: text });

const inheritancePreviewSchema = z.object({
  ok: z.array(previewItemSchema.extend({ fromForm: z.boolean().default(false) })).default([]),
  blocked: z.array(previewItemSchema.extend({ needsReading: text })).default([]),
  notApplicable: z.array(previewItemSchema).default([]),
  calibrations: z.array(text).default([]),
});

export const analysedMachineSchema = z.object({
  equipmentId: text,
  cellId: text,
  layoutId: text,
  parentEquipmentId: nullableText,
  position: z.object({ x: z.number(), y: z.number() }).default({ x: 0, y: 0 }),
  label: text.default(''),
  tag: text,
  name: text.default(''),
  drawnCode: text,
  storedCode: nullableText,
  storedSource: lenientOrNull(STORED_SOURCES),
  recordNodeType: nullableText,
  typeCode: text,
  drawnAs: z.object({ code: text, name: text }).nullable().default(null),
  inDatabase: z.boolean(),
  inLibrary: z.boolean(),
  registryEntryId: nullableText,
  libraryVersion: nullableNumber,
  stale: z.boolean().default(false),
  form: z.array(answerSchema).default([]),
  derived: z.record(text, z.number()).default({}),
  flags: z.record(text, z.union([text, z.boolean()])).default({}),
  facts: z.array(text).default([]),
  readings: z.array(machineReadingViewSchema).default([]),
  preview: inheritancePreviewSchema.nullable().default(null),
  connections: z.object({ to: z.array(text).default([]), from: z.array(text).default([]) }).default({ to: [], from: [] }),
});

const analysedLibrarySchema = z.object({
  readings: z.array(libraryReadingSchema).default([]),
  alerts: z.array(libraryAlertSchema).default([]),
  schedules: z.array(libraryScheduleSchema).default([]),
  conditionJobs: z.array(conditionJobSchema).default([]),
});

export const analysedTypeSchema = z.object({
  code: text,
  key: nullableText,
  name: text,
  archetype: lenientOrNull(ARCHETYPES),
  tier: lenientOrNull(TIERS),
  confirm: nullableText,
  inLibrary: z.boolean(),
  version: nullableNumber,
  library: analysedLibrarySchema.nullable().default(null),
  nextPhase: nextPhaseSchema.nullable().default(null),
  machineIds: z.array(text).default([]),
});

const warningSchema = z.object({
  code: lenient(WARNING_CODES),
  cellId: text.optional(),
  equipmentId: text.optional(),
  message: text,
});

export const scadaAnalysisSchema = z.object({
  plantId: text,
  layoutId: nullableText,
  layoutVersion: nullableNumber,
  nestedLayoutIds: z.array(text).default([]),
  analysedAt: instant,
  paletteVersion: nullableNumber,
  libraryVersion: nullableNumber,
  durationMs: z.number().default(0),
  honourShapeDefaults: z.boolean().default(true),
  summary: z.object({
    machines: z.number().default(0),
    equipmentTypes: z.number().default(0),
    readings: z.number().default(0),
    nextPhaseReadings: z.number().default(0),
    drawnNotEquipment: z.number().default(0),
    connections: z.number().default(0),
    notInDatabase: z.number().default(0),
    unknownReadings: z.number().default(0),
    archivedSkipped: z.number().default(0),
  }),
  site: z.object({ label: text }).nullable().default(null),
  types: z.array(analysedTypeSchema).default([]),
  machines: z.array(analysedMachineSchema).default([]),
  drawnNotEquipment: z.array(z.object({ cellId: text, code: text, name: text, label: text.default('') })).default([]),
  warnings: z.array(warningSchema).default([]),
});

const violationSchema = z.object({ path: text.default(''), code: text, message: text.default('') });

export const applyResultSchema = z.object({
  equipmentId: text,
  outcome: lenient(APPLY_OUTCOMES),
  message: text.optional(),
  violations: z.array(violationSchema).optional(),
});

export const scadaApplySchema = scadaAnalysisSchema.extend({ results: z.array(applyResultSchema).default([]) });
